import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { mergeCartsAfterAuth } from '../lib/mergeCartsAfterAuth';
import { dispatchCartUpdate } from '../lib/pendingCart';
import { useToast } from '../hooks/use-toast';
import { isCartReturnState } from '../lib/cartNavigation';
import PersonalDataConsent from '../components/PersonalDataConsent';
import MarketingConsent from '../components/MarketingConsent';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const returnState = location.state?.from;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }

    if (!consent) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }

    setLoading(true);

    const result = await register(email, password, consent, marketingConsent);
    
    if (result.success) {
      await new Promise((resolve) => setTimeout(resolve, 150));

      try {
        const r = await mergeCartsAfterAuth();
        dispatchCartUpdate();

        const totalAdded = r.pending.ok + r.guest.ok;
        const totalFail = r.pending.fail + r.guest.fail;
        const hadWork = r.pending.total > 0 || r.guest.total > 0;

        if (hadWork) {
          if (totalFail === 0 && totalAdded > 0) {
            toast({
              title: 'Заказ добавлен в корзину',
              description:
                totalAdded === 1
                  ? 'Позиция перенесена в корзину'
                  : `Успешно добавлено позиций: ${totalAdded}`,
            });
          } else if (totalAdded > 0 && totalFail > 0) {
            toast({
              title: 'Частично добавлено',
              description: `Добавлено ${totalAdded}, не удалось перенести: ${totalFail}. Остальное можно добавить вручную.`,
              variant: 'default',
            });
          } else if (totalFail > 0) {
            toast({
              title: 'Ошибка',
              description:
                'Не удалось перенести выбранные позиции в корзину. Добавьте решение снова после регистрации.',
              variant: 'destructive',
            });
          }
        }

        const restoreStep = returnState?.restoreStep ?? returnState?.step ?? 1;
        if (returnState?.pathname === '/checkout') {
          navigate('/checkout', { state: { restoreStep } });
        } else if (isCartReturnState(returnState)) {
          navigate('/dashboard?tab=cart');
        } else if (hadWork) {
          navigate('/dashboard?tab=cart');
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error merging cart after register:', error);
        toast({
          title: 'Ошибка',
          description: error.message || 'Не удалось обновить корзину.',
          variant: 'destructive',
        });
        if (returnState?.pathname === '/checkout') {
          navigate('/checkout', { state: { restoreStep: returnState.restoreStep ?? 1 } });
        } else {
          navigate('/dashboard?tab=cart');
        }
      }
    } else {
      setError(result.error || 'Ошибка при регистрации');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead title="Регистрация | Антология" noindex />
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-16 pt-24">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Регистрация</CardTitle>
            <CardDescription>
              Создайте аккаунт для доступа к личному кабинету
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Электронная почта</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="example@mail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Пароль</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Минимум 6 символов"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Повторите пароль"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <PersonalDataConsent
                id="register-pd-consent"
                checked={consent}
                onCheckedChange={(v) => setConsent(Boolean(v))}
              />

              <MarketingConsent
                id="register-marketing-consent"
                className="mt-3"
                checked={marketingConsent}
                onCheckedChange={(v) => setMarketingConsent(Boolean(v))}
              />

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Регистрация...' : 'Зарегистрироваться'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">Уже есть аккаунт? </span>
              <Link to="/login" state={{ from: returnState }} className="text-accent hover:underline">
                Войти
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterPage;
